import React, { useMemo, useState } from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { VerifiedUserOutlined } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionCard, StatusChip } from '../../components/ui';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { lensFocalRangeApi } from '../../services/lensFocalRangeApi';
import { tokens } from '../../theme/tokens';
import { asDate, systemJson } from './api';
import { DryRunResult, RunDrawer, SystemScript } from './RunDrawer';

interface ReleaseStep {
  id: string;
  title: string;
  description: string;
  when: string;
  flag: string | null;
  ticket: string | null;
  script: string;
  params: Array<{ key: string; label: string; type: 'text' | 'boolean'; default: string | boolean; help?: string }>;
  lastApply: { at: string | null; by: string; byEmail: string | null; changeCount: number } | null;
}

interface ReleaseList {
  stack: { projectId: string; billingRegion: 'US' | 'CA' };
  steps: ReleaseStep[];
}

interface NormStatus {
  collections: Record<string, { total: number; processed: number; pending: number }>;
  canonicalProducts: number;
}

const COLLECTIONS = ['all', 'products', 'cameraProducts', 'accessoryProducts'];
const FORCE_BRANDS = ['', 'ProMaster', 'Canon', 'Sony', 'Nikon', 'Fujifilm'];
const IMPORT_BRANDS = [
  { value: 'canon', label: 'Canon' },
  { value: 'sony', label: 'Sony' },
  { value: 'nikon', label: 'Nikon' },
  { value: 'fujifilm', label: 'Fujifilm' },
  { value: 'promaster', label: 'ProMaster' },
];

function normScriptId(options: Record<string, string | boolean>): string {
  const collection = String(options.collection || 'all');
  const brand = String(options.brand || '');
  if (collection === 'all' && options.force && brand) return 'normalization:brand-force';
  if (collection !== 'all') return 'normalization:collection';
  return 'normalization:all';
}

interface NormCopy {
  wouldChange: (count: number) => string;
  detail: (processed: number, skipped: number, failed: number) => string;
}

async function runNormalization(path: string, body: Record<string, unknown>, aborted: () => boolean, copy: NormCopy): Promise<DryRunResult> {
  if (aborted()) throw new Error('Stopped');
  const data = await systemJson<Record<string, unknown> & { created: number; updated: number; processed: number; skipped: number; failed: number; dryRun?: { id: string; hash: string; changeCount: number } }>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const changeCount = (data.dryRun?.changeCount ?? data.created + data.updated) || 0;
  return {
    changeCount,
    dryRunId: data.dryRun?.id || '',
    dryRunHash: data.dryRun?.hash || '',
    summary: copy.wouldChange(changeCount),
    detail: copy.detail(data.processed, data.skipped, data.failed),
    rows: [],
    notes: [],
    walkComplete: true,
    columns: 'none',
  };
}

export const ScriptsTab: React.FC<{ regionLabel: string }> = ({ regionLabel }) => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<SystemScript | null>(null);
  const release = useQuery({
    queryKey: ['admin-release-steps'],
    queryFn: () => systemJson<ReleaseList>('/admin/release-steps'),
  });
  const status = useQuery({
    queryKey: ['admin-normalization-status'],
    queryFn: () => systemJson<NormStatus>('/normalization/status'),
  });

  const scripts = useMemo(() => {
    const normCopy: NormCopy = {
      wouldChange: (count) => t('admin.systemV2.scripts.wouldChange', { count }),
      detail: (processed, skipped, failed) => t('admin.systemV2.scripts.normDetail', { processed, skipped, failed }),
    };
    const list: SystemScript[] = [];
    (release.data?.steps || []).forEach((step, index) => {
      list.push({
        id: `release:${step.id}`,
        historyId: () => `release:${step.id}`,
        group: 'release',
        groupTitle: t('admin.systemV2.scripts.groups.release'),
        name: step.title,
        description: step.description,
        when: step.when,
        chips: [step.flag, step.ticket, step.script].filter(Boolean).map((label) => ({ label: String(label) })),
        number: index + 1,
        applied: Boolean(step.lastApply),
        options: step.params.map((param) => ({
          key: param.key,
          label: param.label,
          type: param.type,
          defaultValue: param.default,
          help: param.help,
        })),
        destructive: () => false,
        fallbackHistory: step.lastApply
          ? { at: step.lastApply.at, by: step.lastApply.byEmail || step.lastApply.by, changeCount: step.lastApply.changeCount }
          : null,
        dryRun: async (options) => {
          const data = await systemJson<{ outcome: { changeCount: number; notes: string[]; rows: Array<{ id: string; title: string; action: string }> }; dryRun: { id: string; hash: string } }>(
            `/admin/release-steps/${step.id}/preview`,
            { method: 'POST', body: JSON.stringify({ params: options }) },
          );
          return {
            changeCount: data.outcome.changeCount,
            dryRunId: data.dryRun.id,
            dryRunHash: data.dryRun.hash,
            summary: t('admin.systemV2.scripts.wouldChange', { count: data.outcome.changeCount }),
            rows: data.outcome.rows.map((row) => ({ id: row.id, title: row.title, action: row.action })),
            notes: data.outcome.notes || [],
            walkComplete: true,
            columns: 'action',
          };
        },
        apply: async (options, receipt) => {
          await systemJson(`/admin/release-steps/${step.id}/apply`, {
            method: 'POST',
            body: JSON.stringify({ params: options, dryRun: receipt }),
          });
          queryClient.invalidateQueries({ queryKey: ['admin-release-steps'] });
        },
      });
    });

    list.push({
      id: 'normalization:products',
      historyId: normScriptId,
      group: 'normalization',
      groupTitle: t('admin.systemV2.scripts.groups.normalization'),
      name: t('admin.systemV2.scripts.normalize'),
      description: t('admin.systemV2.scripts.normalizeDesc'),
      when: t('admin.systemV2.scripts.normalizeWhen'),
      chips: [{ label: 'normalization' }],
      applied: false,
      destructive: (options) => Boolean(options.force),
      options: [
        { key: 'collection', label: t('admin.systemV2.scripts.collection'), type: 'select', defaultValue: 'all', choices: COLLECTIONS.map((value) => ({ value, label: value === 'all' ? t('admin.systemV2.scripts.allCollections') : value })) },
        { key: 'brand', label: t('admin.systemV2.scripts.brand'), type: 'select', defaultValue: '', choices: FORCE_BRANDS.map((value) => ({ value, label: value || t('admin.systemV2.scripts.anyBrand') })) },
        { key: 'force', label: t('admin.systemV2.scripts.force'), type: 'boolean', defaultValue: false },
      ],
      dryRun: (options, aborted) => {
        const collection = String(options.collection || 'all');
        const path = collection === 'all' ? '/normalization/process' : `/normalization/process/${collection}`;
        return runNormalization(path, { dryRun: true, force: Boolean(options.force), brand: options.brand || undefined, batchSize: 100 }, aborted, normCopy);
      },
      apply: (options, receipt, aborted) => {
        const collection = String(options.collection || 'all');
        const path = collection === 'all' ? '/normalization/process' : `/normalization/process/${collection}`;
        return runNormalization(path, { dryRun: false, force: Boolean(options.force), brand: options.brand || undefined, batchSize: 100, scriptDryRun: receipt }, aborted, normCopy).then(() => undefined);
      },
    });

    list.push({
      id: 'normalization:brand-import',
      historyId: () => 'normalization:brand-import',
      group: 'normalization',
      groupTitle: t('admin.systemV2.scripts.groups.normalization'),
      name: t('admin.systemV2.scripts.importBrand'),
      description: t('admin.systemV2.scripts.importDesc'),
      when: t('admin.systemV2.scripts.importWhen'),
      chips: [{ label: 'raw import' }],
      applied: false,
      destructive: () => false,
      options: [{ key: 'brandId', label: t('admin.systemV2.scripts.brand'), type: 'select', defaultValue: 'canon', choices: IMPORT_BRANDS }],
      dryRun: (options, aborted) => runNormalization(`/normalization/process/brand/${options.brandId}`, { dryRun: true, batchSize: 100 }, aborted, normCopy),
      apply: (options, receipt, aborted) => runNormalization(`/normalization/process/brand/${options.brandId}`, { dryRun: false, batchSize: 100, scriptDryRun: receipt }, aborted, normCopy).then(() => undefined),
    });

    list.push({
      id: 'lens:focal-range',
      historyId: () => 'lens:focal-range',
      group: 'catalog',
      groupTitle: t('admin.systemV2.scripts.groups.catalog'),
      name: t('admin.systemV2.scripts.lens'),
      description: t('admin.systemV2.scripts.lensDesc'),
      when: t('admin.systemV2.scripts.lensWhen'),
      chips: [{ label: 'lens_focal_range' }, { label: 'MOT-452' }],
      applied: false,
      destructive: () => false,
      options: [],
      dryRun: async (_options, aborted) => {
        let cursor: string | null = null;
        let session: { id: string; hash: string; changeCount: number } | undefined;
        const rows: DryRunResult['rows'] = [];
        let page;
        do {
          if (aborted()) {
            return {
              changeCount: session?.changeCount || 0,
              dryRunId: session?.id || '',
              dryRunHash: session?.hash || '',
              summary: t('admin.systemV2.scripts.stopped'),
              rows,
              notes: [],
              walkComplete: false,
              columns: 'beforeAfter',
            };
          }
          page = await lensFocalRangeApi.backfillPage({ dryRun: true, cursor, dryRunSession: session ? { id: session.id, hash: session.hash } : undefined });
          session = page.dryRunSession;
          for (const sample of page.samples) {
            if (rows.length < 20) {
              rows.push({
                id: sample.id,
                title: sample.name,
                before: sample.before ? `${sample.before.focalClass}${sample.before.lensKind ? ` ${sample.before.lensKind}` : ''}` : '—',
                after: sample.after ? `${sample.after.focalClass}${sample.after.lensKind ? ` ${sample.after.lensKind}` : ''}` : '—',
                action: sample.action,
              });
            }
          }
          cursor = page.nextCursor;
        } while (cursor);
        return {
          changeCount: session?.changeCount || 0,
          dryRunId: session?.id || '',
          dryRunHash: session?.hash || '',
          summary: t('admin.systemV2.scripts.wouldChange', { count: session?.changeCount || 0 }),
          rows,
          notes: [],
          walkComplete: true,
          columns: 'beforeAfter',
        };
      },
      apply: async (_options, receipt, aborted) => {
        let cursor: string | null = null;
        do {
          if (aborted()) throw new Error(t('admin.systemV2.scripts.stopped'));
          const page = await lensFocalRangeApi.backfillPage({ dryRun: false, cursor, dryRunSession: receipt });
          cursor = page.nextCursor;
        } while (cursor);
      },
    });
    return list;
  }, [release.data, t, queryClient]);

  const normTotals = status.data
    ? Object.values(status.data.collections).reduce(
        (sum, item) => ({ total: sum.total + item.total, processed: sum.processed + item.processed, pending: sum.pending + item.pending }),
        { total: 0, processed: 0, pending: 0 },
      )
    : null;
  const releaseSteps = release.data?.steps || [];
  const releaseApplied = releaseSteps.filter((step) => step.lastApply).length;

  const groups: Array<{ id: SystemScript['group']; title: string; summary?: string; description: string }> = [
    {
      id: 'release',
      title: t('admin.systemV2.scripts.groups.release'),
      summary: release.data ? t('admin.systemV2.scripts.releaseSummary', { applied: releaseApplied, total: releaseSteps.length }) : undefined,
      description: t('admin.systemV2.scripts.groupHelp.release'),
    },
    {
      id: 'normalization',
      title: t('admin.systemV2.scripts.groups.normalization'),
      summary: normTotals
        ? normTotals.pending
          ? t('admin.systemV2.scripts.normPendingSummary', { count: normTotals.pending, formatted: normTotals.pending.toLocaleString() })
          : t('admin.systemV2.scripts.normDone')
        : undefined,
      description: t('admin.systemV2.scripts.groupHelp.normalization'),
    },
    { id: 'catalog', title: t('admin.systemV2.scripts.groups.catalog'), description: t('admin.systemV2.scripts.groupHelp.catalog') },
  ];

  const statusFor = (script: SystemScript) => {
    if (script.group !== 'release') return null;
    const last = script.fallbackHistory;
    if (last) {
      const when = asDate(last.at);
      return {
        tone: 'positive' as const,
        label: when
          ? t('admin.systemV2.scripts.appliedOn', { when: when.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) })
          : t('admin.systemV2.scripts.appliedStatus'),
        meta: t('admin.systemV2.scripts.appliedMeta', { who: last.by, count: last.changeCount }),
      };
    }
    return { tone: 'caution' as const, label: t('admin.systemV2.scripts.notApplied'), meta: t('admin.systemV2.scripts.waiting') };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          px: 2,
          py: 1.5,
          border: `1px solid ${tokens.accent.tintStrong}`,
          borderRadius: `${tokens.radius.card}px`,
          bgcolor: tokens.signal.info.row,
        }}
      >
        <VerifiedUserOutlined sx={{ fontSize: 18, color: tokens.accent.main, mt: '1px', flex: '0 0 auto' }} />
        <Typography sx={{ fontSize: 13, color: tokens.ink.primary }}>
          {t('admin.systemV2.scripts.rule')} {regionLabel}
        </Typography>
      </Box>
      {groups.map((group) => {
        const rows = scripts.filter((script) => script.group === group.id);
        return (
          <SectionCard key={group.id} dense>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, px: '20px', py: 1.75, borderBottom: `1px solid ${tokens.line.hairline}` }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap' }}>
                <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600 }}>{group.title}</Typography>
                {group.summary && <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{group.summary}</Typography>}
              </Box>
              <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>{group.description}</Typography>
              {group.id === 'normalization' && normTotals && normTotals.total > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round((normTotals.processed / normTotals.total) * 100)}
                      aria-label={t('admin.systemV2.scripts.groups.normalization')}
                      sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: tokens.line.default, '& .MuiLinearProgress-bar': { bgcolor: tokens.accent.main } }}
                    />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {Math.round((normTotals.processed / normTotals.total) * 100)}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '6px 20px', flexWrap: 'wrap', fontSize: 12.5, color: tokens.ink.bodyAlt }}>
                    {[
                      [normTotals.total, t('admin.systemV2.scripts.normParts.legacy')],
                      [normTotals.processed, t('admin.systemV2.scripts.normParts.linked')],
                      [normTotals.pending, t('admin.systemV2.scripts.normParts.pending')],
                      [status.data?.canonicalProducts ?? 0, t('admin.systemV2.scripts.normParts.canonical')],
                    ].map(([value, label]) => (
                      <span key={String(label)}>
                        <Box component="strong" sx={{ fontWeight: 600, color: tokens.ink.primary, fontVariantNumeric: 'tabular-nums' }}>
                          {Number(value).toLocaleString()}
                        </Box>{' '}
                        {label}
                      </span>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
            {rows.map((script) => {
              const state = statusFor(script);
              const numbered = script.number !== undefined;
              const tile = numbered ? (script.applied ? tokens.signal.positive : tokens.signal.caution) : null;
              return (
                <Box
                  key={script.id}
                  onClick={() => setOpen(script)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '28px minmax(0, 1fr)', sm: '28px minmax(0, 1fr) auto' },
                    gap: '8px 14px',
                    alignItems: 'start',
                    px: '20px',
                    py: 1.75,
                    borderTop: `1px solid ${tokens.line.hairline}`,
                    '&:first-of-type': { borderTop: 0 },
                    cursor: 'pointer',
                    '&:hover': { bgcolor: tokens.surface.subtle },
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '7px',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 11.5,
                      fontWeight: 700,
                      bgcolor: tile ? tile.tint : 'transparent',
                      color: tile ? tile.ink : tokens.ink.disabled,
                    }}
                  >
                    {script.number ?? '•'}
                  </Box>
                  <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.625 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{script.name}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{script.when}</Typography>
                    {script.chips.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', pt: 0.25 }}>
                        {script.chips.map((chip) => (
                          <StatusChip
                            key={chip.label}
                            tone="neutral"
                            label={chip.label}
                            sx={chip.label.includes('/') || chip.label.endsWith('.ts') ? { fontFamily: tokens.font.mono, fontSize: 11.5 } : undefined}
                          />
                        ))}
                      </Box>
                    )}
                    {state && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px 10px', flexWrap: 'wrap', pt: 0.25 }}>
                        <StatusChip tone={state.tone} dot label={state.label} />
                        <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{state.meta}</Typography>
                      </Box>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpen(script);
                    }}
                    sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, justifySelf: { xs: 'start', sm: 'auto' } }}
                  >
                    {t('admin.systemV2.scripts.run')}
                  </Button>
                </Box>
              );
            })}
          </SectionCard>
        );
      })}
      <RunDrawer script={open} onClose={() => setOpen(null)} />
    </Box>
  );
};
